import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { useToast } from '../components/shared/ToastNotification';
import Text from '../components/shared/Text';

function TaxRatesIndex() {
    const [taxRates, setTaxRates] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRate, setEditingRate] = useState<any>(null);
    const [formData, setFormData] = useState({ name: '', percentage: '' });
    const toast = useToast();

    useEffect(() => {
        loadTaxRates();
    }, []);

    const loadTaxRates = async () => {
        try {
            setLoading(true);
            const response = await api.get('/tax_rates');
            setTaxRates(response.data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (rate: any) => {
        setEditingRate(rate);
        setFormData({ name: rate.name, percentage: rate.percentage.toString() });
        setIsModalOpen(true);
    };

    const handleAdd = () => {
        setEditingRate(null);
        setFormData({ name: '', percentage: '' });
        setIsModalOpen(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const data = {
                tax_rate: {
                    name: formData.name,
                    percentage: parseFloat(formData.percentage)
                }
            };

            let response;
            if (editingRate) {
                response = await api.put(`/tax_rates/${editingRate.id}`, data);
            } else {
                response = await api.post('/tax_rates', data);
            }

            if (response.success) {
                toast.success(`Tax rate ${editingRate ? 'updated' : 'created'} successfully`);
                setIsModalOpen(false);
                loadTaxRates();
            } else {
                toast.error(response.errors?.join(', ') || 'Failed to save tax rate');
            }
        } catch (err) {
            console.error(err);
            toast.error('An error occurred');
        }
    };

    const handleDelete = async (id: number) => {
        if (!confirm('Are you sure you want to delete this tax rate?')) return;
        try {
            const response = await api.delete(`/tax_rates/${id}`);
            if (response.success) {
                toast.success('Tax rate deleted');
                loadTaxRates();
            }
        } catch (err) {
            console.error(err);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="flex items-center justify-between mb-8">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <Link to="/settings" className="text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg>
                        </Link>
                        <Text variant="h1" color="primary">Tax <span className="text-indigo-600">Registry</span></Text>
                    </div>
                    <Text variant="body" color="slate">Configure custom GST slots and tax rates for your firm</Text>
                </div>
                <button
                    onClick={handleAdd}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 transition-all shadow-sm shadow-indigo-200"
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" /></svg>
                    New Tax Slot
                </button>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <table className="w-full text-left border-collapse text-sm">
                    <thead>
                        <tr className="bg-slate-50/50 border-b border-slate-100">
                            <th className="px-6 py-4"><Text variant="label" color="slate">Tax Name</Text></th>
                            <th className="px-6 py-4"><Text variant="label" color="slate">Percentage</Text></th>
                            <th className="px-6 py-4"><Text variant="label" color="slate">Associated Products</Text></th>
                            <th className="px-6 py-4 text-right"><Text variant="label" color="slate">Actions</Text></th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {loading ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-8 text-center text-slate-400 italic">Synchronizing rates...</td>
                            </tr>
                        ) : taxRates.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-6 py-12 text-center">
                                    <Text variant="body" color="slate" weight="medium">No custom tax rates defined yet.</Text>
                                </td>
                            </tr>
                        ) : (
                            taxRates.map((rate) => (
                                <tr key={rate.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="px-6 py-4">
                                        <Text variant="body" weight="semibold" color="primary">{rate.name}</Text>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Text variant="mono" weight="bold" className="text-indigo-600">{rate.percentage}%</Text>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex flex-col">
                                            <Text variant="body" weight="medium" color="slate">
                                                {rate.products_count || 0} Products
                                            </Text>
                                            {rate.sample_products && rate.sample_products.length > 0 && (
                                                <Text variant="label" className="text-slate-400 truncate max-w-xs">
                                                    e.g. {rate.sample_products.join(', ')}
                                                </Text>
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => handleEdit(rate)}
                                                className="p-1.5 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                            </button>
                                            <button
                                                onClick={() => handleDelete(rate.id)}
                                                className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-300">
                        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                            <Text variant="h3" color="primary">{editingRate ? 'Modify' : 'New'} Tax Slot</Text>
                            <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div>
                                <Text variant="label" color="slate" className="mb-1 block">Label / Name</Text>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. GST 18%, IGST 12%"
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-medium"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <Text variant="label" color="slate" className="mb-1 block">Percentage (%)</Text>
                                <input
                                    type="number"
                                    required
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="0.00"
                                    className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all font-mono font-bold"
                                    value={formData.percentage}
                                    onChange={(e) => setFormData({ ...formData, percentage: e.target.value })}
                                />
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 py-2 bg-white border border-slate-200 text-slate-600 rounded-lg font-bold text-sm hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 py-2 bg-indigo-600 text-white rounded-lg font-bold text-sm hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-200"
                                >
                                    {editingRate ? 'Update Rate' : 'Save Tax Slot'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default TaxRatesIndex;
